import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  LayoutDashboard, 
  Package, 
  FolderOpen, 
  MessageSquare, 
  LogOut,
  Menu,
  X,
  Database,
  Settings,
  Home,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminLayout = ({ children }) => {
  const { isAuthenticated, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/beheer');
      return;
    }
    fetchStats();
  }, [isAuthenticated, navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSeedData = async () => {
    try {
      await axios.post(`${API}/admin/seed`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Voorbeeld data geladen');
      fetchStats();
      window.location.reload();
    } catch (error) {
      console.error('Error seeding data:', error);
      toast.error('Fout bij laden data');
    }
  };

  const navItems = [
    { path: '/beheer/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/beheer/producten', label: 'Producten', icon: Package },
    { path: '/beheer/categorieen', label: 'Categorieën', icon: FolderOpen },
    { path: '/beheer/berichten', label: 'Berichten', icon: MessageSquare, badge: stats?.unread_messages },
    { path: '/beheer/instellingen', label: 'Instellingen', icon: Settings },
    { path: '/beheer/certificaten', label: 'Certificaten', icon: FileText },
  ];

  const isActive = (path) => location.pathname === path;

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b px-4 py-3 flex items-center justify-between">
        <span className="font-bold text-gray-800">Admin Panel</span>
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside 
          className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform lg:translate-x-0 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="p-5 border-b border-gray-200 hidden lg:block">
            <h1 className="font-bold text-xl text-gray-800">Alphaprofiles</h1>
            <p className="text-gray-500 text-sm">Beheer Panel</p>
          </div>
          
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-[#E31E24] text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <item.icon className="h-5 w-5" />
                {item.label}
                {item.badge > 0 && (
                  <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${
                    isActive(item.path) ? 'bg-white text-[#E31E24]' : 'bg-[#E31E24] text-white'
                  }`}>
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 space-y-2">
            <Button 
              variant="outline"
              className="w-full justify-start text-gray-600 border-gray-200"
              onClick={handleSeedData}
            >
              <Database className="h-4 w-4 mr-2" />
              Demo Data Laden
            </Button>
            <Link to="/" className="block">
              <Button 
                variant="outline"
                className="w-full justify-start text-gray-600 border-gray-200"
              >
                <Home className="h-4 w-4 mr-2" />
                Naar Website
              </Button>
            </Link>
            <Button 
              variant="outline"
              className="w-full justify-start text-gray-600 border-gray-200"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Uitloggen
            </Button>
          </div>
        </aside>

        {/* Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 min-h-screen">
          <div className="max-w-6xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
